import { axiosV2, getError, getResponse, settings } from '../../helpers/UtilsHelper';

const { app } = settings;
const { backend } = app;
const { urls } = backend;

export const AccountService = {
    /**
     * Get all user accounts
     * @param {Object} params
     * @param {Object} params.dataObj
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    getAll: async ({
        dataObj = {},
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.getAll';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/`,
                data: dataObj,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Delete an account
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    delete: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.delete';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.delete({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/${id}`,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Save/Create a Mercado Libre account
     * @param {Object} params
     * @param {Object} params.account
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    save: async ({
        account = {},
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.save';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/`,
                data: account,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Get a specific account details
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    get: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.get';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.get({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/${id}`,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Update an account details
     * @param {Object} params
     * @param {string} params.id
     * @param {Object} params.account
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    update: async ({
        id = '',
        account = {},
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.update';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/${id}`,
                data: account,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Get Mercado Libre account information
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    getInformation: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.getInformation';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id,
                method: backend?.methods?.users?.getInformation
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/`,
                data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Generate Mercado Libre integration token
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    generateToken: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.generateToken';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id,
                method: backend?.methods?.users?.generateToken
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/`,
                data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Clear Mercado Libre token transaction/authorization logs
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    clearTokenLogs: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'AccountService.clearTokenLogs';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id,
                method: backend?.methods?.users?.clearTokenLogs
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/`,
                data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    }
};
